import SuiteProductLevelIdentifiersMap from '../../../../constants/merchCards/SuiteProductLevelIdentifiersMap';
import { STARTER_CUSTOMER_PLATFORM_UPSELL_MERCH_CARD } from '../../../../constants/tracking/TrackingSources';
import { useGlobalLoading } from '../../../../hooks/loadingHooks';
import { useAppSelector } from '../../../../reducers';
import { isAppAuthSelector } from '../../../../selectors/AuthSelectors';
import { isUngatedForInbound25Selector } from '../../../../selectors/GatingSelectors';
import { packagedQuantityBySkuIdSelector } from '../../../../selectors/ProductsSelectors';
import { useQueryParamTerm } from '../../../../utils/urls/appQueryParamUtils';
import { MerchCardLayoutUI } from '../../../../v2/components/MerchCardLayoutUI';
import SkeletonLoadWrapper from '../../../common/loading/SkeletonLoadWrapper';
import { IncludedHubSpotCredits } from '../../../../v2/components/merch-card/credits-display/IncludedHubSpotCredits';
import { StarterCustomerPlatformUpsellCardFeatures } from '../../features-display/StarterCustomerPlatformUpsellCardFeatures';
import CrmSuitePriceMerchCard from '../../price-display/CrmSuitePriceMerchCard';
import { useCrmSuiteBundleSkuId } from '../../price-display/useCrmSuitePricingInfo';
import { SubtextDisplayContainerV1 } from '../../subtext-display/SubtextDisplayContainerV1';
import { useSkuAdditionalUnits } from '../../useSkuAdditionalUnits';
import { SuiteCardCTAs } from './SuiteCardCTAs';
import { SuiteCardSubheading } from './SuiteCardSubheading';
import { SuiteCardTerm } from './SuiteCardTerm';
import { SuiteCardTitle } from './SuiteCardTitle';

/*
 * This card is rendered on the public starter tab on hub pages
 * and for free TB portals in app.
 * Therefore there is no pre-seats version of this card.
 */
import { jsx as _jsx } from "react/jsx-runtime";
const UPSELL_PRODUCT_LEVEL = 'STARTER';

/*
 * Because the upsell card is only shown for free portals & public, and is STARTER,
 * we will always want to show the price per month per seat like we do for all
 * STARTER products on free portals & public
 */
const SHOW_PRICE_PER_MONTH_PER_SEAT = true;
export const UpsellSuiteCard = ({
  productLine,
  screen,
  columnStart
}) => {
  const isAppReady = useGlobalLoading();
  const isAppAuth = useAppSelector(isAppAuthSelector);
  const {
    queryParamTerm
  } = useQueryParamTerm();
  const isUngatedForInbound25 = useAppSelector(isUngatedForInbound25Selector);

  /*
   * This is the skuId for product line the upsell card is currently showing,
   * not the skuId for the bundle product
   * So if you're on the marketing page, this will be the marketing contacts skuId
   */
  const {
    additionalUnitSkuId
  } = useSkuAdditionalUnits({
    productLevel: UPSELL_PRODUCT_LEVEL,
    productLine
  });
  const suiteSkuId = useCrmSuiteBundleSkuId({
    productLevel: UPSELL_PRODUCT_LEVEL
  });
  const includedProductLevelHubspotCredits = useAppSelector(state => {
    if (!suiteSkuId) return null;
    return packagedQuantityBySkuIdSelector(state, {
      skuId: suiteSkuId,
      unitName: 'HUBSPOT_CREDITS'
    });
  });
  const trackingSource = STARTER_CUSTOMER_PLATFORM_UPSELL_MERCH_CARD;
  const Credits = /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
    isLoading: !isAppReady,
    heights: [34],
    widths: ['75%'],
    children: !!includedProductLevelHubspotCredits && isUngatedForInbound25 && /*#__PURE__*/_jsx(IncludedHubSpotCredits, {
      includedProductLevelHubspotCredits: includedProductLevelHubspotCredits,
      trackingSource: trackingSource
    })
  });
  const Subtext = /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
    isLoading: !isAppReady,
    heights: [34],
    widths: ['75%'],
    children: /*#__PURE__*/_jsx(SubtextDisplayContainerV1, {
      additionalUnitSkuId: additionalUnitSkuId,
      queryParamTerm: queryParamTerm,
      productLine: productLine,
      productLevel: UPSELL_PRODUCT_LEVEL
    })
  });
  const Features = /*#__PURE__*/_jsx(StarterCustomerPlatformUpsellCardFeatures, {
    currentProductLine: productLine,
    loading: !isAppReady
  });
  return /*#__PURE__*/_jsx(MerchCardLayoutUI, {
    "data-test-id": `${SuiteProductLevelIdentifiersMap[UPSELL_PRODUCT_LEVEL]}-merch-card`,
    "data-selenium-id": `${SuiteProductLevelIdentifiersMap[UPSELL_PRODUCT_LEVEL]}-merch-card`,
    columnStart: columnStart,
    Title: /*#__PURE__*/_jsx(SuiteCardTitle, {
      productLevel: UPSELL_PRODUCT_LEVEL
    }),
    Subheading: /*#__PURE__*/_jsx(SuiteCardSubheading, {
      productLevel: UPSELL_PRODUCT_LEVEL
    }),
    Pricing: /*#__PURE__*/_jsx(CrmSuitePriceMerchCard, {
      productLevel: UPSELL_PRODUCT_LEVEL,
      showPricePerMonthPerSeat: SHOW_PRICE_PER_MONTH_PER_SEAT
    }),
    Term: /*#__PURE__*/_jsx(SuiteCardTerm, {
      productLevel: UPSELL_PRODUCT_LEVEL,
      trackingSource: trackingSource
    }),
    Credits: Credits,
    Subtext: Subtext,
    CTAs: /*#__PURE__*/_jsx(SuiteCardCTAs, {
      productLevel: UPSELL_PRODUCT_LEVEL,
      screen: screen,
      trackingSource: trackingSource,
      returnFirstCTAOnly: isAppAuth
    }),
    Features: Features,
    isUngatedForInbound25: isUngatedForInbound25
  });
};